import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { Button } from '@common/Button';
import { Input } from '@common/FormFields';
import { logError, logInfo } from '@utils/logger';

export const DeleteSurveyModal = ({ isOpen, onClose, survey }) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !survey) return null;

  const handleDelete = async () => {
    if (confirmText.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Delete the survey document
      await deleteDoc(doc(db, 'surveys', survey.id));
      
      logInfo(`Survey ${survey.id} deleted successfully`);
      
      // Close the modal
      onClose();
      
      // Reload the page to refresh the survey list
      window.location.reload();
    } catch (err) {
      logError('Failed to delete survey:', err);
      setError('Failed to delete survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[400] p-4">
      <div className="bg-[#2A2B2E] rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Delete Survey</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-red-500/10 p-4 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-red-500 mb-2">Warning: This action cannot be undone</h3>
              <p className="text-gray-300">
                You are about to delete the survey <span className="font-medium text-white">"{survey.name}"</span>. 
                This will permanently remove all survey data, including routes, POIs, and files.
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-gray-300">To confirm, please type <span className="font-medium text-white">delete</span> below:</p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type 'delete' to confirm"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              loading={loading}
              disabled={confirmText.toLowerCase() !== 'delete' || loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Deleting...' : 'Delete Survey'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};